package demos.buttons

import androidx.compose.animation.core.*
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Favorite
import androidx.compose.material.icons.rounded.FavoriteBorder
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.*


const val LIKE_BUTTON = "89a9bf2159f97365ecfdc79acec96da4f2bb45bffd71a68bcefbf7a429adce41"

private val red = Color(0xFFf5516f)
private val gray = Color(0xFF959595)
private val confettiColors = listOf(
    Color(0xffa864fd),
    Color(0xff29cdff),
    Color(0xff78ff44),
    Color(0xffff718d),
    Color(0xfffdff6a),
)

@Composable
fun LikeButton() {

    var isLiked by remember { mutableStateOf(false) }
    var touched by remember { mutableStateOf(false) }
    val scale by animateFloatAsState(
        targetValue = if (touched) .85f else 1f, animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessMedium,
        )
    )

    Box(
        Modifier
            .pointerInput(Unit) {
                detectTapGestures(
                    onPress = {
                        touched = true
                        awaitRelease()
                        touched = false
                    }
                ) {
                    isLiked = !isLiked
                }
            }
            .size(48.dp)
            .scale(scale),
        contentAlignment = Alignment.Center,
    ) {

        val ringScale = remember { Animatable(0f) }
        val ringWidth = remember { Animatable(0f) }
        val ringAlpha = remember { Animatable(0f) }
        val heartSize = remember { Animatable(0f) }
        val particleOffset = remember { Animatable(0f) }
        val particleSize = remember { Animatable(0f) }
        val lineOffset = remember { Animatable(0f) }
        val lineSize = remember { Animatable(0f) }
        val lineAlpha = remember { Animatable(0f) }

        LaunchedEffect(isLiked) {
            if (isLiked) {
                ringAlpha.snapTo(1f)
                ringScale.snapTo(0f)
                ringWidth.snapTo(30f)
                heartSize.snapTo(0f)
                particleOffset.snapTo(0f)
                particleSize.snapTo(1f)
                lineOffset.snapTo(0f)
                lineSize.snapTo(0f)
                lineAlpha.snapTo(1f)

                ringScale.animateTo(2f)
                launch {
                    particleOffset.animateTo(
                        1f,
                        spring(dampingRatio = Spring.DampingRatioNoBouncy, stiffness = Spring.StiffnessLow)
                    )
                    particleSize.animateTo(0f)
                }

                launch {
                    lineOffset.animateTo(100f)
                }
                launch {
                    lineSize.animateTo(1f)
                    lineSize.animateTo(.07f)
                    lineAlpha.animateTo(0f)
                }

                launch {
                    delay(100)
                    heartSize.animateTo(
                        1f,
                        spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow)
                    )
                }
                ringWidth.animateTo(1f)
                launch {
                    ringWidth.animateTo(0f)
                }
                ringAlpha.animateTo(0f)
            } else {
                launch { lineAlpha.animateTo(0f) }
                launch { particleSize.animateTo(0f) }
                launch { ringAlpha.animateTo(0f) }
            }
        }

        val numberOfParticles = remember { 30 }
        for (i in 1..numberOfParticles) {
            Box(
                Modifier
                    .offset {
                        val offset = if (i % 2 == 0) -50f else 0f
                        val variance = if (i % 2 == 0) 1.5f else 1f
                        val angle = ((360f / numberOfParticles) * i) + offset

                        val radius = particleOffset.value * (300f + offset) * variance

                        val x = radius * sin(PI * 2 * (angle / 360));

                        val y = radius * cos(PI * 2 * (angle / 360));

                        IntOffset(x.toInt(), y.toInt())
                    }
                    .scale(particleSize.value)
                    .size(5.dp)
                    .background(confettiColors[i % confettiColors.size], CircleShape)
            )
        }

        val numberOfLines = remember { 18 }
        for (i in 1..numberOfLines) {
            val angle = ((360f / numberOfLines) * i)

            Row(
                Modifier
                    .rotate(angle)
                    .offset(x = lineOffset.value.dp)
                    .height(1.dp)
                    .fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                Box(Modifier
                    .fillMaxHeight()
                    .fillMaxWidth(lineSize.value)
                    .alpha(lineAlpha.value)
                    .background(
                        color = Color.White,
                        shape = CircleShape
                    )
                )
            }
        }

        Box(
            modifier = Modifier
                .fillMaxSize()
                .scale(ringScale.value)
                .alpha(ringAlpha.value)
                .border(ringWidth.value.dp, red, CircleShape)
        )

        if (!isLiked) {
            Image(
                imageVector = Icons.Rounded.FavoriteBorder,
                contentDescription = "like",
                modifier = Modifier.fillMaxSize(),
                colorFilter = ColorFilter.tint(gray),
            )
        }

        if (isLiked) {
            Image(
                imageVector = Icons.Rounded.Favorite,
                contentDescription = "like",
                modifier = Modifier.scale(heartSize.value).fillMaxSize(),
                colorFilter = ColorFilter.tint(red),
            )
        }
    }
}